import type { AppRouteRecordRaw } from "../types/types";

import { RouteNames } from "@/app/router/types/names";
import { MCP_TABS } from "@/features/admin/mcp/constants";
import { PermissionGroups, Permissions } from "@/shared/constants";

const adminRoutes: AppRouteRecordRaw[] = [
  {
    path: "/admin",
    name: "admin-parent",
    redirect: "/admin/users",
    meta: {
      menuIcon: "lucide:shield",
      menuTitle: "Admin",
      isMenuParent: true,
      permissions: PermissionGroups.ADMIN,
    },
    children: [
      {
        path: "users",
        name: RouteNames.USERS,
        meta: {
          permissions: [Permissions.ADMIN],
          title: "Users Management",
          menuTitle: "Users",
          menuIcon: "lucide:users",
          partnerships: ["seller", "vendor"],
          filters: {
            show: [],
          },
        },
        component: () => import("@/pages/Admin/UsersList.vue"),
      },
      {
        path: "/ai",
        name: "ai-parent",
        redirect: "/ai/usage",
        meta: {
          menuIcon: "lucide:sparkles",
          menuTitle: "AI Features",
          isMenuParent: true,
          permissions: PermissionGroups.AI_FEATURES,
        },
        children: [
          {
            path: "/ai/features",
            name: RouteNames.AI_FEATURES,
            meta: {
              // Read gates the screen; the write permissions are checked per control inside it.
              // Turning a feature off in an emergency must not imply authority to rewrite what
              // the model says.
              permissions: [Permissions.READ_LLM_USAGE],
              title: "AI Features",
              menuTitle: "Features",
              menuIcon: "lucide:sliders-horizontal",
              partnerships: ["seller", "vendor"],
              filters: {
                show: [],
              },
            },
            component: () => import("@/pages/Admin/LlmFeatures.vue"),
          },
          {
            path: "/ai/usage",
            name: RouteNames.AI_USAGE,
            meta: {
              permissions: [Permissions.READ_LLM_USAGE],
              title: "AI Usage & Cost",
              menuTitle: "Usage & Cost",
              menuIcon: "lucide:receipt",
              partnerships: ["seller", "vendor"],
              filters: {
                show: [],
              },
            },
            component: () => import("@/pages/Admin/AiUsage.vue"),
          },
          {
            path: "/ai/generations",
            name: RouteNames.AI_GENERATIONS,
            meta: {
              permissions: [Permissions.READ_LLM_USAGE],
              title: "AI Generations",
              menuTitle: "Generations",
              menuIcon: "lucide:list",
              partnerships: ["seller", "vendor"],
              filters: { show: [] },
            },
            component: () => import("@/pages/Admin/AiGenerations.vue"),
          },

        ],
      },
      {
        // One screen for everything MCP; the summary and the call list are its tabs. They share
        // the window, the tool and the status filter, and the by-user drill-down runs between
        // them — as two routes that drill-down cost a full page remount and had to carry the
        // window in the query string to survive it.
        //
        // Still its own menu entry rather than a child of `ai-parent`: MCP is a different budget,
        // and it is admin-only where AI Features is permission-gated, so nesting it there would
        // put an admin-only screen behind a grantable group.
        path: "/mcp-usage",
        name: RouteNames.MCP_USAGE,
        meta: {
          // Admin-only, matching @AdminOnly() on McpUsageController: the report spans every
          // account — byUser returns colleagues' names and emails, and /calls returns the
          // filters each colleague queried.
          permissions: [Permissions.ADMIN],
          // Names the whole screen, not the tab that happens to open first. "Usage & Cost" was
          // the old summary-only route's heading; leaving it would label the Calls tab with the
          // title of its sibling.
          title: "MCP Usage & Calls",
          menuTitle: "MCP",
          menuIcon: "lucide:plug",
          partnerships: ["seller", "vendor"],
          // The window comes from the global picker rather than one of the screen's own: it is
          // the same `{ since, until }` the MCP endpoints take, and it brings URL sync, presets
          // and request aborting that a local picker would have to re-implement.
          //
          // Showing it also shows the global-filters button, and with brands/channels/products
          // off the drawer holds only Reset and Saved Filters. That is deliberate — the reset is
          // wanted, and the drawer is where the next MCP-wide filter will go.
          filters: { show: ["dateRange"] },
        },
        component: () => import("@/pages/Admin/McpUsage.vue"),
      },
      {
        // The two retired routes. VTabs mirrors the active tab into the hash, so a bookmark of
        // the old Calls screen lands on the Calls tab rather than on a 404.
        //
        // `showInMenu: false` is not optional here: a route with neither `menuTitle` nor `title`
        // nor `name` still reaches `routesToMenuItems`, which labels it "Untitled" — two of those
        // would appear in the sidebar beside the real entry.
        path: "/mcp-usage/summary",
        redirect: { path: "/mcp-usage", hash: `#tab-${MCP_TABS.summary}` },
        meta: {
          permissions: [Permissions.ADMIN],
          showInMenu: false,
          partnerships: ["seller", "vendor"],
        },
      },
      {
        path: "/mcp-usage/calls",
        redirect: { path: "/mcp-usage", hash: `#tab-${MCP_TABS.calls}` },
        meta: {
          permissions: [Permissions.ADMIN],
          showInMenu: false,
          partnerships: ["seller", "vendor"],
        },
      },
      {
        // One screen for everything email-related; sending, resends and deliveries are its tabs.
        // The group gates the screen; Resends and Deliveries additionally gate themselves inside
        // the feature.
        path: "/communication",
        name: RouteNames.COMMUNICATION,
        meta: {
          permissions: PermissionGroups.COMMUNICATION,
          title: "Communication",
          menuTitle: "Communication",
          menuIcon: "lucide:mail",
          partnerships: ["seller", "vendor"],
          filters: {
            show: [],
          },
        },
        component: () => import("@/pages/Admin/MailCommunication.vue"),
      },
      {
        path: "users/:userId",
        name: RouteNames.EDIT_USER,
        meta: {
          permissions: [Permissions.ADMIN],
          title: "Edit User",
          showInMenu: false,
          partnerships: ["seller", "vendor"],
          filters: {
            show: [],
          },
        },
        component: () => import("@/pages/Admin/UserInfo.vue"),
      },
    ],
  },

];

export default adminRoutes;
